import { create } from 'zustand';

// Matches backend NotificationResponse struct (serde camelCase)
export interface Notification {
  notificationId: number;
  userId: number;
  notificationType: string;
  category: string;
  priority: string;
  title: string;
  message: string;
  sourceType: string | null;
  sourceId: number | null;
  isRead: boolean;
  readAt: string | null;
  // Sweep-4 P05: no isDismissed — the backend never implemented it.
  expiresAt: string | null;
  createdAt: string;
}

/**
 * One machine-consumed CCOW `context.changed` event, carried OUTSIDE the bell
 * inbox. The inbox path formats every event to human-readable prose at ingest,
 * which made the structured payload unrecoverable past that point and killed
 * cross-app patient-context follow (the hook JSON.parsed prose). The payload
 * rides here verbatim instead, and `useContextSync` consumes it from here —
 * never by peeking at `notifications[0]`, which any interleaving notification
 * could displace.
 */
export interface ContextEvent {
  /** Monotonic per-page-load sequence; consumers track the last processed one. */
  seq: number;
  /** The raw WS `payload` (a snake_case ContextState), untouched by formatting. */
  payload: unknown;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  /**
   * The socket is terminally down: reconnects exhausted, or the server refused
   * auth. Distinct from `!isConnected`, which is also true while connecting or
   * between backoff retries — those transient states must not raise the banner.
   */
  connectionDown: boolean;
  /**
   * The server refused the token (`auth_error`). Retrying with the same token
   * cannot help; the user must log in again. Cleared on the next open.
   */
  authFailed: boolean;
  contextEvent: ContextEvent | null;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  setNotifications: (notifications: Notification[]) => void;
  setConnected: (connected: boolean) => void;
  setConnectionDown: (down: boolean) => void;
  setAuthFailed: (failed: boolean) => void;
  publishContextEvent: (payload: unknown) => void;
  removeNotification: (notificationId: number) => void;
}

let contextSeq = 0;

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  connectionDown: false,
  authFailed: false,
  contextEvent: null,
  addNotification: (notification) => {
    set(state => ({
      notifications: [notification, ...state.notifications].slice(0, 100),
      unreadCount: state.unreadCount + (notification.isRead ? 0 : 1),
    }));
  },
  markAsRead: (notificationId) => {
    // Decrement ONLY when a previously-unread item actually matched. The REST
    // inboxes call this with server row ids while the bell store was keyed by
    // a per-page-load counter, so the map matched nothing — yet the badge
    // still dropped by one per call, deflating the count for items the bell
    // still showed unread (clamped at 0, so the error hid).
    set(state => {
      const target = state.notifications.find(n => n.notificationId === notificationId);
      if (!target || target.isRead) return state;
      return {
        notifications: state.notifications.map(n =>
          n.notificationId === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },
  markAllAsRead: () => {
    set(state => ({
      notifications: state.notifications.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
      unreadCount: 0,
    }));
  },
  setNotifications: (notifications) => {
    set({
      notifications,
      unreadCount: notifications.filter(n => !n.isRead).length,
    });
  },
  setConnected: (connected) => set({ isConnected: connected }),
  setConnectionDown: (down) => set({ connectionDown: down }),
  setAuthFailed: (failed) => set({ authFailed: failed }),
  publishContextEvent: (payload) => {
    contextSeq += 1;
    set({ contextEvent: { seq: contextSeq, payload } });
  },
  removeNotification: (notificationId) => {
    set(state => {
      const removed = state.notifications.find(n => n.notificationId === notificationId);
      return {
        notifications: state.notifications.filter(n => n.notificationId !== notificationId),
        unreadCount: removed && !removed.isRead ? state.unreadCount - 1 : state.unreadCount,
      };
    });
  },
}));
